import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Project } from 'app/modules/projects/projects.types';
import { concat, map, Observable } from 'rxjs';

@Component({
  selector: 'app-choose-parent-portfolio',
  templateUrl: './choose-parent-portfolio.component.html',
  styleUrls: ['./choose-parent-portfolio.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ChooseParentPortfolioComponent extends OnDestroyMixin implements OnInit {
  addProjectForm: UntypedFormGroup;
  projects: Project[];
  parentProject: Project;
  childProjects: Project[];
  projectsToDelete: Project[];
  loadingChildren = false;
  saving = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public readonly matDialogRef: MatDialogRef<ChooseParentPortfolioComponent>,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly projectsService: ProjectsService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.projects = this.data.projects || [];
    this.parentProject = this.data.parentProject;
    this.childProjects = this.data.childProjects;

    this.addProjectForm = this.formBuilder.group({
      selectedProject: [
        { value: this.parentProject?.id || '', disabled: !!this.parentProject?.id },
        [Validators.required],
      ],
      childIds: ['', [Validators.required]],
    });

    if (this.parentProject?.id && this.childProjects?.length) {
      this.loadChildren();
    }

    this.addProjectForm.controls.selectedProject.valueChanges.pipe(untilComponentDestroyed(this)).subscribe((value) => {
      if (value) {
        this.parentProject = this.projects.find((project) => project.id === value);
        this.childProjects = this.projects.filter((project) => project.id !== value);
        this.loadChildren();
      }
    });
  }

  save(): void {
    const operationResult = {
      selectedParentProject: this.addProjectForm.value.selectedProject,
      childIds: this.childProjects.map((project) => project.id),
    };
    this.saving = true;
    this.projectsService
      .saveProject({ ...this.parentProject, child_ids: operationResult.childIds })
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => {
        //Delete the projects that are not needed.
        if (this.projectsToDelete?.length > 0) {
          this.projectsToDelete.map((project) => {
            this.projectsService.deleteProject(project.id).subscribe();
          });
        }
        this.projectsToDelete = [];
        this.matDialogRef.close({ success: true });
        this.saving = false;
      });
  }

  discard(): void {
    this.matDialogRef.close();
  }

  get childIds(): string[] {
    return this.addProjectForm.get('childIds').value;
  }

  private loadChildren(): void {
    this.loadingChildren = true;
    //Updating the projects that we need to delete on the save.
    this.projectsToDelete = this.childProjects;
    this.prepareObservables([this.parentProject, ...this.childProjects])
      .pipe(untilComponentDestroyed(this))
      .subscribe({
        next: (allChildrenProjects) => {
          //SC-39717, we will be deleting the other portfolios so no need to add to children
          this.childProjects = allChildrenProjects;
          this.addProjectForm.controls.childIds.setValue(this.childProjects.map((project) => project.id));
        },
        error: () => {
          this.loadingChildren = false;
          this.addProjectForm.controls.childIds.setValue('');
          this.addProjectForm.markAllAsTouched();
          this.addProjectForm.updateValueAndValidity();
        },
        complete: () => {
          this.loadingChildren = false;
        },
      });
  }

  private prepareObservables(projects: Project[]): Observable<Project[]> {
    //SC-39717, we will be getting the children for the parent portfolios in a single call.
    const projectIds = projects.map((project) => project.id);
    return this.projectsService
      .getProjects(
        ['title', 'id', 'parent_id'],
        { fjson: `[["parent_id","in",[${projectIds}]]]` },
        0,
        500,
        'title',
        'asc',
        '',
        null,
        '',
        false,
        false,
      )
      .pipe(map((response) => response.projects));
  }
}
